import { axiosGetV3 } from '../../api/axios-handler';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getSessionSeed } from '../../utils/sessionSeed';
import { Content, Resource, People } from './useExperiences';
import { fetchVimeoMetadataBatch, addVimeoMetadataToExperiences } from './useBrowseExperiences';
import { addVimeoMetadataToCollectionItems, ContentCollection } from './useContentCollection';

export const LISTENER_AUDIO_PAGE_LIMIT = 20;
export const RESOURCES_PAGE_LIMIT = 20;
export const PEERS_PAGE_LIMIT = 20;

// Re-export types for convenience
export type ContentByTopicListenerAudio = Content;
export type ContentByTopicResource = Resource;
export type ContentByTopicListenerRole = People;

export interface ContentByTopicCount {
  listener_audio: number;
  client_resources: number;
  global_resources: number;
  listener_roles: number;
  /** Total resources (client + global) when paginated */
  resources?: number;
}

export interface ContentByTopicData {
  listener_audio: ContentByTopicListenerAudio[];
  client_resources: ContentByTopicResource[];
  global_resources: ContentByTopicResource[];
  listener_roles: ContentByTopicListenerRole[];
  content_collections?: ContentCollection[];
}

export interface ContentByTopicResponse {
  count: ContentByTopicCount;
  data: ContentByTopicData;
}

export interface ContentByTopicOptions {
  page?: number;
  limit?: number;
}

const getContentByTopic = async (
  tagGroupId: number,
  clientId: number,
  searchQuery?: string,
  options?: ContentByTopicOptions,
): Promise<ContentByTopicResponse> => {
  // If there's a search query, use Algolia search endpoint
  if (searchQuery && searchQuery.trim().length > 0) {
    const params: any = {
      q: searchQuery.trim(),
      tag_groups: tagGroupId.toString(),
      seed: getSessionSeed(),
    };

    const response = await axiosGetV3('/simple_search/algolia/search', params);
    const data = response.data;

    // Transform Algolia response to match ContentByTopicResponse format
    // Algolia returns a flat array, we need to categorize by type
    const transformedData: ContentByTopicData = {
      listener_audio: [],
      client_resources: [],
      global_resources: [],
      listener_roles: [],
    };

    // Algolia returns a flat array of content (experiences and resources)
    const allItems = data.data || [];

    // Separate experiences from resources
    const experiences: Content[] = [];
    const resources: Resource[] = [];

    allItems.forEach((item: any) => {
      // Resources have image_url, name, description but no listener_role_id
      // Experiences have listener_role_id, title, file_url
      if (item.listener_role_id && (item.title || item.file_url)) {
        experiences.push(item as Content);
      } else if (item.image_url || (item.name && !item.listener_role_id)) {
        resources.push(item as Resource);
      }
    });

    // Add vimeo metadata to experiences
    const experiencesWithMetadata = await fetchAndAddVimeoMetadataToExperiences(experiences);

    // Categorize experiences and resources
    experiencesWithMetadata.forEach((exp) => {
      transformedData.listener_audio.push(exp);
    });

    resources.forEach((resource) => {
      if (resource.client_id) {
        transformedData.client_resources.push(resource);
      } else {
        transformedData.global_resources.push(resource);
      }
    });

    return {
      count: {
        listener_audio: transformedData.listener_audio.length,
        client_resources: transformedData.client_resources.length,
        global_resources: transformedData.global_resources.length,
        listener_roles: transformedData.listener_roles.length,
      },
      data: transformedData,
    };
  }

  // Otherwise use the regular topic content endpoint
  const params: any = {
    client_id: clientId,
    seed: getSessionSeed(),
  };
  if (options?.page != null) params.page = options.page;
  if (options?.limit != null) params.limit = options.limit;
  const response = await axiosGetV3(`/tag_groups/content/${tagGroupId}`, params);
  const data = response.data;

  // Fetch Vimeo metadata for listener_audio items
  if (data.data?.listener_audio && data.data.listener_audio.length > 0) {
    const listenerAudioWithMetadata = await fetchAndAddVimeoMetadataToExperiences(data.data.listener_audio);
    data.data.listener_audio = listenerAudioWithMetadata;
  }

  if (data.data?.content_collections && data.data.content_collections.length > 0) {
    const collectionsWithMetadata = await Promise.all(
      data.data.content_collections.map(async (collection: ContentCollection) => {
        const itemsWithMetadata = await addVimeoMetadataToCollectionItems(collection.items || []);
        return {
          ...collection,
          items: itemsWithMetadata,
        };
      }),
    );
    return {
      ...data,
      data: {
        ...data.data,
        content_collections: collectionsWithMetadata,
      },
    };
  }

  return data;
};

const fetchAndAddVimeoMetadataToExperiences = async (experiences: Content[]): Promise<Content[]> => {
  const videoIds = Array.from(
    new Set(experiences.map((exp) => exp.video_id).filter((id): id is string => Boolean(id))),
  );

  if (videoIds.length === 0) {
    return experiences;
  }

  const metadataMap = await fetchVimeoMetadataBatch(videoIds);

  return experiences.map((exp) => addVimeoMetadataToExperiences(exp, metadataMap));
};

/** Per-type content fetchers (no search). Each uses its own pagination. */
const buildTopicParams = (clientId: number, page?: number, limit?: number) => {
  const params: Record<string, string | number> = { client_id: clientId };
  if (page != null) params.page = page;
  if (limit != null) params.limit = limit;
  return params;
};

export const getContentByTopicListenerAudio = async (
  tagGroupId: number,
  clientId: number,
  page: number,
  limit: number,
): Promise<{ count: number; data: Content[] }> => {
  const response = await axiosGetV3(
    `/tag_groups/content/${tagGroupId}/listener_audio`,
    buildTopicParams(clientId, page, limit),
  );
  const list = response.data?.data?.listener_audio ?? [];
  const withMetadata = await fetchAndAddVimeoMetadataToExperiences(list as Content[]);
  const count = response.data?.count?.listener_audio ?? withMetadata.length;
  return { count, data: withMetadata };
};

export const getContentByTopicResources = async (
  tagGroupId: number,
  clientId: number,
  page: number,
  limit: number,
): Promise<{ count: number; data: { client_resources: Resource[]; global_resources: Resource[] } }> => {
  const response = await axiosGetV3(
    `/tag_groups/content/${tagGroupId}/resources`,
    buildTopicParams(clientId, page, limit),
  );
  const count = response.data?.count?.resources ?? 0;
  const client_resources = response.data?.data?.client_resources ?? [];
  const global_resources = response.data?.data?.global_resources ?? [];
  return { count, data: { client_resources, global_resources } };
};

export const getContentByTopicListenerRoles = async (
  tagGroupId: number,
  clientId: number,
  page: number,
  limit: number,
): Promise<{ count: number; data: People[] }> => {
  const response = await axiosGetV3(
    `/tag_groups/content/${tagGroupId}/listener_roles`,
    buildTopicParams(clientId, page, limit),
  );
  const list = response.data?.data?.listener_roles ?? [];
  const count = response.data?.count?.listener_roles ?? list.length;
  return { count, data: list };
};

export const getContentByTopicCollections = async (
  tagGroupId: number,
  clientId: number,
): Promise<ContentCollection[]> => {
  const response = await axiosGetV3(
    `/tag_groups/content/${tagGroupId}/content_collections`,
    buildTopicParams(clientId),
  );
  const collections = response.data?.data?.content_collections ?? [];
  if (collections.length === 0) return [];
  const withMetadata = await Promise.all(
    collections.map(async (c: ContentCollection) => ({
      ...c,
      items: await addVimeoMetadataToCollectionItems(c.items || []),
    })),
  );
  return withMetadata;
};

/** Flattened content + pagination helpers for browse-by-topic (infinite scroll + See more). */
export interface ContentByTopicInfiniteData {
  listener_audio: ContentByTopicListenerAudio[];
  client_resources: ContentByTopicResource[];
  global_resources: ContentByTopicResource[];
  listener_roles: ContentByTopicListenerRole[];
  content_collections?: ContentCollection[];
  /** Total listener_audio count from API count payload. */
  listener_audio_total_count: number | null | undefined;
  /** Total listener_roles count from API count payload. */
  listener_roles_total_count: number | null | undefined;
  /** Total resources (client + global) count from API count payload. */
  resources_total_count: number | null | undefined;
}

/** Combined infinite query used only when search is active (Algolia). When search is empty, use the per-type hooks instead. */
export const useContentByTopicInfinite = (tagGroupId?: number, clientId?: number, searchQuery?: string) => {
  const normalizedSearchQuery = searchQuery?.trim() || undefined;
  const isEnabled = !!tagGroupId && !!clientId && !!normalizedSearchQuery;

  const infinite = useInfiniteQuery<ContentByTopicResponse>(
    ['contentByTopicInfinite', tagGroupId, clientId, normalizedSearchQuery],
    async ({ pageParam }) => {
      const page = pageParam == null ? 1 : (pageParam as number);
      return getContentByTopic(tagGroupId!, clientId!, normalizedSearchQuery, {
        page,
        limit: LISTENER_AUDIO_PAGE_LIMIT,
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const limit = LISTENER_AUDIO_PAGE_LIMIT;
        const loaded = allPages.length * limit;
        const audioTotal = lastPage.count?.listener_audio ?? 0;
        const rolesTotal = lastPage.count?.listener_roles ?? 0;
        const resourcesTotal = lastPage.count?.resources ?? 0;
        const hasMore =
          (audioTotal > 0 && loaded < audioTotal) ||
          (rolesTotal > 0 && loaded < rolesTotal) ||
          (resourcesTotal > 0 && loaded < resourcesTotal);
        return hasMore ? allPages.length + 1 : undefined;
      },
      enabled: isEnabled,
    },
  );

  const flattened: ContentByTopicInfiniteData | undefined = infinite.data
    ? {
        listener_audio: infinite.data.pages.flatMap((p: ContentByTopicResponse) => p.data?.listener_audio ?? []),
        client_resources: infinite.data.pages.flatMap((p: ContentByTopicResponse) => p.data?.client_resources ?? []),
        global_resources: infinite.data.pages.flatMap((p: ContentByTopicResponse) => p.data?.global_resources ?? []),
        listener_roles: infinite.data.pages.flatMap((p: ContentByTopicResponse) => p.data?.listener_roles ?? []),
        content_collections: infinite.data.pages[0]?.data?.content_collections,
        listener_audio_total_count: infinite.data.pages[0]?.count?.listener_audio ?? null,
        listener_roles_total_count: infinite.data.pages[0]?.count?.listener_roles ?? null,
        resources_total_count: infinite.data.pages[0]?.count?.resources ?? null,
      }
    : undefined;

  return {
    ...infinite,
    data: flattened,
    flattened,
  };
};

/** Per-type hooks for browse-by-topic (no shared pagination). Disabled when searchQuery is non-empty (use Algolia instead). */
export const useContentByTopicListenerAudioInfinite = (
  tagGroupId?: number,
  clientId?: number,
  searchQuery?: string,
) => {
  const limit = LISTENER_AUDIO_PAGE_LIMIT;
  const hasSearch = Boolean(searchQuery?.trim());
  return useInfiniteQuery(
    ['contentByTopicListenerAudio', tagGroupId, clientId],
    async ({ pageParam = 1 }) => getContentByTopicListenerAudio(tagGroupId!, clientId!, pageParam, limit),
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.length * limit;
        return lastPage.count > loaded ? allPages.length + 1 : undefined;
      },
      enabled: !!tagGroupId && !!clientId && !hasSearch,
    },
  );
};

export const useContentByTopicResourcesInfinite = (tagGroupId?: number, clientId?: number, searchQuery?: string) => {
  const limit = RESOURCES_PAGE_LIMIT;
  const hasSearch = Boolean(searchQuery?.trim());
  return useInfiniteQuery(
    ['contentByTopicResources', tagGroupId, clientId],
    async ({ pageParam = 1 }) => getContentByTopicResources(tagGroupId!, clientId!, pageParam, limit),
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.length * limit;
        return lastPage.count > loaded ? allPages.length + 1 : undefined;
      },
      enabled: !!tagGroupId && !!clientId && !hasSearch,
    },
  );
};

export const useContentByTopicPeersInfinite = (tagGroupId?: number, clientId?: number, searchQuery?: string) => {
  const limit = PEERS_PAGE_LIMIT;
  const hasSearch = Boolean(searchQuery?.trim());
  return useInfiniteQuery(
    ['contentByTopicPeers', tagGroupId, clientId],
    async ({ pageParam = 1 }) => getContentByTopicListenerRoles(tagGroupId!, clientId!, pageParam, limit),
    {
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.length * limit;
        return lastPage.count > loaded ? allPages.length + 1 : undefined;
      },
      enabled: !!tagGroupId && !!clientId && !hasSearch,
    },
  );
};

export const useContentByTopicCollectionsQuery = (tagGroupId?: number, clientId?: number, searchQuery?: string) => {
  const hasSearch = Boolean(searchQuery?.trim());
  return useQuery(
    ['contentByTopicCollections', tagGroupId, clientId],
    () => getContentByTopicCollections(tagGroupId!, clientId!),
    { enabled: !!tagGroupId && !!clientId && !hasSearch },
  );
};
